import { useState, useEffect } from "react";
import { useLocation } from "wouter";
import { useAuth } from "@/contexts/AuthContext";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { ArrowLeft, Crown, Loader2, Check } from "lucide-react";
import { loadStripe } from "@stripe/stripe-js";
import { Elements } from "@stripe/react-stripe-js";
import { SubscribeForm } from "@/components/SubscribeForm";
import { SUBSCRIPTION_PLANS } from "@/types/user";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { PageContainer } from "@/components/layout/PageContainer";

const stripePublicKey = import.meta.env.VITE_STRIPE_PUBLIC_KEY;
const stripePromise = stripePublicKey ? loadStripe(stripePublicKey) : null;

export default function Subscribe() {
  const { user, userProfile, loading, isAuthenticated } = useAuth();
  const [, setLocation] = useLocation();
  const [clientSecret, setClientSecret] = useState<string | null>(null);
  const [selectedPlan, setSelectedPlan] = useState<"premium" | "platinum">("premium");
  const [billingCycle, setBillingCycle] = useState<"monthly" | "yearly">("monthly");
  const [isCreatingIntent, setIsCreatingIntent] = useState(false);

  // Parse URL params for plan and cycle
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const plan = params.get("plan") as "premium" | "platinum" | null;
    const cycle = params.get("cycle") as "monthly" | "yearly" | null;

    if (plan && (plan === "premium" || plan === "platinum")) {
      setSelectedPlan(plan);
    }
    if (cycle && (cycle === "monthly" || cycle === "yearly")) {
      setBillingCycle(cycle);
    }
  }, []);

  useEffect(() => {
    if (!loading && !isAuthenticated) {
      setLocation("/");
    }
  }, [loading, isAuthenticated, setLocation]);

  useEffect(() => {
    if (isAuthenticated && user) {
      createPaymentIntent();
    }
  }, [isAuthenticated, user, selectedPlan, billingCycle]);

  const createPaymentIntent = async () => {
    if (!user) return;

    try {
      setIsCreatingIntent(true);
      const plan = SUBSCRIPTION_PLANS.find(p => p.id === selectedPlan);
      if (!plan) return;

      const amount = billingCycle === "monthly"
        ? Math.round(plan.price * 100)
        : Math.round(plan.priceYearly * 100);

      const response = await fetch("/api/create-payment-intent", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          userId: user.uid,
          email: user.email,
          plan: selectedPlan,
          billingCycle,
          amount,
        }),
      });

      if (!response.ok) {
        throw new Error("Failed to create payment intent");
      }

      const data = await response.json();
      setClientSecret((data as { clientSecret?: string }).clientSecret || null);
    } catch (error) {
      console.error("Error creating payment intent:", error);
    } finally {
      setIsCreatingIntent(false);
    }
  };

  if (loading || !userProfile) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  const isPremium = userProfile.role === "premium";
  const isPlatinum = userProfile.role === "platinum";
  const currentPlan = SUBSCRIPTION_PLANS.find(p => p.id === selectedPlan);

  if (isPlatinum) {
    return (
      <div className="max-w-2xl mx-auto px-4 py-8">
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-16">
            <Crown className="h-16 w-16 text-primary mb-4" />
            <h2 className="text-xl font-semibold mb-2">이미 플래티넘 회원입니다</h2>
            <p className="text-muted-foreground text-center mb-6">
              최고 등급 플랜을 이용 중이십니다.
            </p>
            <Button onClick={() => setLocation("/profile")}>프로필로 이동</Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (!stripePromise || !clientSecret) {
    return (
      <div className="max-w-2xl mx-auto px-4 py-8">
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-16">
            {isCreatingIntent ? (
              <>
                <Loader2 className="h-16 w-16 animate-spin text-primary mb-4" />
                <p className="text-muted-foreground">결제 준비 중...</p>
              </>
            ) : (
              <>
                <p className="text-muted-foreground mb-4">결제 시스템을 불러오는 중입니다...</p>
                <Button onClick={() => setLocation("/pricing")}>요금제로 돌아가기</Button>
              </>
            )}
          </CardContent>
        </Card>
      </div>
    );
  }

  const price = billingCycle === "monthly"
    ? currentPlan?.price || 0
    : (currentPlan?.priceYearly || 0) / 12;
  const totalPrice = billingCycle === "monthly"
    ? currentPlan?.price || 0
    : currentPlan?.priceYearly || 0;

  return (
    <div className="max-w-4xl mx-auto px-4 py-8">
      <Button
        variant="ghost"
        onClick={() => setLocation("/pricing")}
        className="mb-6"
        data-testid="button-back-to-pricing"
      >
        <ArrowLeft className="mr-2 h-4 w-4" />
        요금제로 돌아가기
      </Button>

      <div className="grid md:grid-cols-2 gap-8">
        {/* Plan Selection */}
        <div className="space-y-6">
          <div>
            <h2 className="text-2xl font-bold mb-2">플랜 선택</h2>
            <p className="text-muted-foreground">원하시는 플랜을 선택하세요</p>
          </div>

          {/* Plan Cards */}
          <div className="space-y-4">
            {SUBSCRIPTION_PLANS.filter(p => p.id !== "free").map((plan) => (
              <Card
                key={plan.id}
                className={`cursor-pointer transition-all ${
                  selectedPlan === plan.id
                    ? "border-primary shadow-md"
                    : "hover:border-primary/50"
                }`}
                onClick={() => {
                  if (plan.id !== "free") {
                    setSelectedPlan(plan.id as "premium" | "platinum");
                  }
                }}
              >
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      {plan.id === "platinum" && <Crown className="h-5 w-5 text-primary" />}
                      <CardTitle className="text-xl">{plan.nameKo}</CardTitle>
                    </div>
                    {selectedPlan === plan.id && (
                      <Check className="h-5 w-5 text-primary" />
                    )}
                  </div>
                  <CardDescription>
                    ${billingCycle === "monthly" ? plan.price : (plan.priceYearly / 12).toFixed(2)}/월
                    {billingCycle === "yearly" && ` (연간 $${plan.priceYearly})`}
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2">
                    {plan.features.slice(0, 3).map((feature, idx) => (
                      <div key={idx} className="flex items-center gap-2 text-sm">
                        <Check className="h-4 w-4 text-primary flex-shrink-0" />
                        <span>{feature}</span>
                      </div>
                    ))}
                    {!plan.hasAds && (
                      <Badge variant="default" className="bg-green-600">
                        광고 없음
                      </Badge>
                    )}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>

          {/* Billing Cycle */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">결제 주기</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <div
                className={`p-4 rounded-md border-2 cursor-pointer transition-colors ${
                  billingCycle === "monthly"
                    ? "border-primary bg-primary/5"
                    : "border-border hover:border-primary/50"
                }`}
                onClick={() => setBillingCycle("monthly")}
              >
                <div className="flex items-center justify-between">
                  <div>
                    <div className="font-medium">월간 결제</div>
                    <div className="text-sm text-muted-foreground">
                      ${currentPlan?.price || 0}/월
                    </div>
                  </div>
                  {billingCycle === "monthly" && (
                    <Check className="h-5 w-5 text-primary" />
                  )}
                </div>
              </div>

              <div
                className={`p-4 rounded-md border-2 cursor-pointer transition-colors ${
                  billingCycle === "yearly"
                    ? "border-primary bg-primary/5"
                    : "border-border hover:border-primary/50"
                }`}
                onClick={() => setBillingCycle("yearly")}
              >
                <div className="flex items-center justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <span className="font-medium">연간 결제</span>
                      <Badge variant="default" className="bg-green-600">
                        2개월 무료
                      </Badge>
                    </div>
                    <div className="text-sm text-muted-foreground">
                      ${((currentPlan?.priceYearly || 0) / 12).toFixed(2)}/월 (총 ${currentPlan?.priceYearly || 0}/년)
                    </div>
                    <div className="text-sm text-green-600 font-medium mt-1">
                      ${((currentPlan?.price || 0) * 12 - (currentPlan?.priceYearly || 0)).toFixed(2)} 절약
                    </div>
                  </div>
                  {billingCycle === "yearly" && (
                    <Check className="h-5 w-5 text-primary" />
                  )}
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Payment Form */}
        <div>
          <Card>
            <CardHeader>
              <CardTitle className="text-2xl flex items-center gap-2">
                {selectedPlan === "platinum" && <Crown className="h-6 w-6 text-primary" />}
                {currentPlan?.nameKo} 구독
              </CardTitle>
              <CardDescription>
                {billingCycle === "monthly" ? "월간" : "연간"} ${totalPrice.toFixed(2)}
                {billingCycle === "yearly" && " - 2개월 무료"}
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="mb-6 p-4 bg-secondary rounded-md">
                <h3 className="font-semibold mb-2">
                  {currentPlan?.nameKo} 혜택
                </h3>
                <ul className="text-sm text-muted-foreground space-y-1">
                  {currentPlan?.features.map((feature, idx) => (
                    <li key={idx}>• {feature}</li>
                  ))}
                </ul>
                {currentPlan?.hasAds && (
                  <p className="text-sm text-muted-foreground mt-2">
                    ℹ️ 이 플랜은 광고가 포함되어 있습니다
                  </p>
                )}
              </div>

              <Elements stripe={stripePromise} options={{ clientSecret }}>
                <SubscribeForm plan={selectedPlan} billingCycle={billingCycle} />
              </Elements>

              <p className="text-xs text-muted-foreground text-center mt-4">
                결제는 Stripe를 통해 안전하게 처리됩니다
              </p>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
